/**
 * Run builder card — POSTs to the SA-211 analysis-run API. Dates are raw
 * YYYY-MM-DD strings: inclusive calendar days in the DEPLOYMENT timezone
 * (export_range_bounds contract, byte-identical to the conversations
 * export control) — no client-side timezone math. The body is built with
 * explicit keys only: the backend request model is extra="forbid" and
 * structurally strict per report type (SA-237), so each mode sends ONLY
 * its own keys. Deep-dive mode (SA-237) swaps the date/channel/persona
 * selection for a conversation-ids textarea; `initialDeepDiveIds` is the
 * Investigate-button prefill (router state via ConversationAnalysisPage).
 */
import { useState, type FormEvent } from 'react'

import { Button, Input, Textarea, useToast } from '@/components/ui'
import { usePermission } from '@/hooks/usePermission'
import { usePersonas } from '@/hooks/usePersonas'
import { useCreateAnalysisRun } from '@/hooks/useConversationAnalysis'
import { analysisErrorMessage, CHANNEL_OPTIONS, toDateInput } from './runFormat'

const DEEP_DIVE_MAX = 25 // mirrors backend DEEP_DIVE_MAX_CONVERSATIONS

export function RunBuilder({
  initialDeepDiveIds,
}: {
  initialDeepDiveIds?: string[]
}) {
  const canRun = usePermission('conversation_analysis:run')
  const { personas } = usePersonas()
  const createRun = useCreateAnalysisRun()
  const { addToast } = useToast()
  const [start, setStart] = useState(() => {
    const now = new Date()
    return toDateInput(new Date(now.getFullYear(), now.getMonth(), 1))
  })
  const [end, setEnd] = useState(() => toDateInput(new Date()))
  const [channels, setChannels] = useState<string[]>([])
  const [personaIds, setPersonaIds] = useState<string[]>([])
  const [reportType, setReportType] = useState(
    initialDeepDiveIds?.length ? 'deep_dive' : 'quality',
  )
  const [idsText, setIdsText] = useState(() =>
    (initialDeepDiveIds ?? []).join('\n'),
  )
  const [focus, setFocus] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)

  if (!canRun) return null

  const isDeepDive = reportType === 'deep_dive'
  // one per line or comma-separated; de-dup client-side (server re-checks)
  const conversationIds = [
    ...new Set(idsText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean)),
  ]
  const idsInvalid =
    isDeepDive &&
    (conversationIds.length === 0 || conversationIds.length > DEEP_DIVE_MAX)
  const rangeInvalid = !isDeepDive && (!start || !end || end < start)
  const toggle = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (rangeInvalid || idsInvalid) return
    setCreateError(null)
    createRun.mutate(
      isDeepDive
        ? {
            report_type: 'deep_dive',
            conversation_ids: conversationIds,
            focus: focus.trim() ? focus.trim() : null,
          }
        : {
            report_type: reportType,
            start,
            end,
            channels: channels.length ? channels : null,
            persona_ids: personaIds.length ? personaIds : null,
            focus: focus.trim() ? focus.trim() : null,
          },
      {
        onSuccess: () =>
          addToast({
            type: 'success',
            title: 'Analysis started',
            description: 'The run appears in the list below and updates as it progresses.',
          }),
        onError: (err) => setCreateError(analysisErrorMessage(err)),
      },
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-sm border border-border bg-card p-4 space-y-3"
    >
      <h2 className="text-sm font-semibold text-foreground">New report</h2>
      {!isDeepDive && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="analysis-start" className="block text-xs text-muted-foreground mb-1">
                Start
              </label>
              <Input
                id="analysis-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="analysis-end" className="block text-xs text-muted-foreground mb-1">
                End
              </label>
              <Input
                id="analysis-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Inclusive days in the deployment timezone. Test, archived, and copilot
            conversations are always excluded.
          </p>
          <fieldset>
            <legend className="text-xs text-muted-foreground mb-1">Channels (none = all)</legend>
            <div className="flex flex-wrap gap-3">
              {CHANNEL_OPTIONS.map((ch) => (
                <label key={ch} className="flex items-center gap-1.5 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={channels.includes(ch)}
                    onChange={() => setChannels((prev) => toggle(prev, ch))}
                  />
                  {ch}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="text-xs text-muted-foreground mb-1">Personas (none = all)</legend>
            <div className="max-h-36 overflow-y-auto space-y-1 rounded-sm border border-border p-2">
              {personas.map((p) => (
                <label key={p.id} className="flex items-center gap-1.5 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={personaIds.includes(p.id)}
                    onChange={() => setPersonaIds((prev) => toggle(prev, p.id))}
                  />
                  {p.name}
                </label>
              ))}
              {personas.length === 0 && (
                <p className="text-xs text-muted-foreground">No personas.</p>
              )}
            </div>
          </fieldset>
        </>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="analysis-report-type" className="block text-xs text-muted-foreground mb-1">
            Report type
          </label>
          <select
            id="analysis-report-type"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="w-full rounded-sm border border-border bg-background px-3 py-2 text-sm text-foreground"
          >
            <option value="quality">Quality</option>
            <option value="deep_dive">Deep dive</option>
            {/* campaign arrives with SA-213 */}
          </select>
        </div>
        <div>
          <label htmlFor="analysis-focus" className="block text-xs text-muted-foreground mb-1">
            Focus (optional)
          </label>
          <Textarea
            id="analysis-focus"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
            maxLength={2000}
            rows={2}
            placeholder="e.g. delivery complaints, payment friction"
          />
        </div>
      </div>
      {isDeepDive && (
        <div>
          <label
            htmlFor="analysis-conversation-ids"
            className="block text-xs text-muted-foreground mb-1"
          >
            Conversation IDs (one per line or comma-separated)
          </label>
          <Textarea
            id="analysis-conversation-ids"
            value={idsText}
            onChange={(e) => setIdsText(e.target.value)}
            rows={5}
            placeholder={'conversation-id-1\nconversation-id-2'}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {conversationIds.length} of {DEEP_DIVE_MAX} — full transcripts are
            analyzed, so deep dives are bounded to {DEEP_DIVE_MAX} conversations.
          </p>
        </div>
      )}
      {isDeepDive && conversationIds.length > DEEP_DIVE_MAX && (
        <p className="text-xs text-destructive">
          Deep dive accepts at most {DEEP_DIVE_MAX} conversation ids.
        </p>
      )}
      {rangeInvalid && start && end && (
        <p className="text-xs text-destructive">End date must not be before start date.</p>
      )}
      {createError && (
        <p role="alert" className="text-sm text-destructive">{createError}</p>
      )}
      <Button
        type="submit"
        size="sm"
        disabled={rangeInvalid || idsInvalid || createRun.isPending}
        className="text-xs"
      >
        {createRun.isPending ? 'Starting…' : 'Run analysis'}
      </Button>
    </form>
  )
}
